// src/components/Globe/ImprovedRanking.jsx
import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiBarChart2, 
  FiAward, 
  FiAlertTriangle, 
  FiDollarSign, 
  FiMapPin,
  FiThermometer,
  FiDroplet,
  FiWind,
  FiCheckCircle,
  FiTrendingUp,
  FiClock
} from 'react-icons/fi';
import './ImprovedRanking.css';

// Função para formatar timestamp de forma amigável
const formatLastUpdate = (timestamp) => {
  if (!timestamp) return 'Sem dados';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `${diffMins} min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays < 7) return `${diffDays}d atrás`;
    
    // Se for mais antigo, mostrar data formatada
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: '2-digit', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  } catch (error) {
    return 'Data inválida';
  }
};

const ImprovedRanking = ({ sensors, activeLayer, onSensorClick }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  // Processar dados para diferentes visualizações
  const processedData = useMemo(() => {
    // Usar todos os sensores disponíveis (mesmo mockados)
    const validSensors = sensors.filter(s => {
      // Incluir sensores com dados ou sensores mockados
      return s.hasData !== false && (s.quality !== undefined || s.temperature !== undefined || s.co2 !== undefined || s.aqi !== undefined);
    });
    
    // Top Performers (melhores qualidades)
    const topPerformers = [...validSensors]
      .sort((a, b) => {
        if (activeLayer === 'airQuality') {
          return (a.aqi || 999) - (b.aqi || 999);
        }
        return (b.quality || 0) - (a.quality || 0);
      })
      .slice(0, 10);
    
    // Alertas (precisam de atenção)
    const alerts = [...validSensors]
      .filter(s => {
        if (activeLayer === 'airQuality') {
          return s.aqi > 100;
        }
        return s.quality < 40 || s.co2 > 1000 || s.temperature > 30 || s.temperature < 15;
      })
      .slice(0, 5);
    
    // Estatísticas gerais
    const stats = {
      totalActive: validSensors.length,
      avgQuality: validSensors.length > 0 
        ? validSensors.reduce((acc, s) => acc + (s.quality || 0), 0) / validSensors.length 
        : 0,
      avgTemp: validSensors.length > 0
        ? validSensors.reduce((acc, s) => acc + (s.temperature || 0), 0) / validSensors.length
        : 0,
      avgCO2: validSensors.length > 0
        ? validSensors.reduce((acc, s) => acc + (s.co2 || 0), 0) / validSensors.length
        : 0,
      totalEarnings: validSensors.reduce((acc, s) => acc + (s.earnings || 0), 0),
      // Última atualização: pegar a mais recente entre todos os sensores
      lastUpdate: validSensors.length > 0
        ? validSensors
            .map(s => s.lastUpdate)
            .filter(Boolean)
            .sort((a, b) => new Date(b) - new Date(a))[0] || null
        : null
    };
    
    // Leaderboard por earnings
    const earningsLeaders = [...validSensors]
      .filter(s => s.wallet_address && s.earnings > 0)
      .sort((a, b) => (b.earnings || 0) - (a.earnings || 0))
      .slice(0, 5);
    
    // Distribuição por cidade e estado
    const cityDistribution = validSensors.reduce((acc, sensor) => {
      let city = sensor.city;
      let state = sensor.state || sensor.state_code || '';
      
      // Se cidade não estiver definida, usar business_name ou location_name
      if (!city || city.trim() === '' || city === 'Unknown') {
        city = sensor.business_name || sensor.location_name || 'Local Desconhecido';
      }
      
      // Se todos os sensores têm a mesma cidade genérica (ex: "Brasília"), 
      // diferenciar por business_name ou location_name
      if (city === 'Brasília' || city === 'Brazil') {
        const locationName = sensor.business_name || sensor.location_name;
        if (locationName && locationName !== city) {
          // Tentar extrair cidade do nome do local se possível
          const cityMatch = locationName.match(/([A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ\s]+)/);
          if (cityMatch && cityMatch[1].trim() !== city) {
            city = cityMatch[1].trim();
          } else {
            // Usar o nome do local como diferenciador
            city = locationName;
          }
        }
      }
      
      const locationKey = state ? `${city} - ${state}` : city;
      
      if (!acc[locationKey]) {
        acc[locationKey] = { 
          count: 0, 
          sensors: [], 
          avgQuality: 0,
          city: city,
          state: state
        };
      }
      acc[locationKey].count++;
      acc[locationKey].sensors.push(sensor);
      acc[locationKey].avgQuality += sensor.quality || 0;
      return acc;
    }, {});
    
    // Calcular médias por cidade
    Object.keys(cityDistribution).forEach(locationKey => {
      cityDistribution[locationKey].avgQuality = 
        cityDistribution[locationKey].avgQuality / cityDistribution[locationKey].count;
    });
    
    return {
      topPerformers,
      alerts,
      stats,
      earningsLeaders,
      cityDistribution
    };
  }, [sensors, activeLayer]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiBarChart2 },
    { id: 'quality', label: 'Top Quality', icon: FiAward },
    { id: 'alerts', label: 'Alerts', icon: FiAlertTriangle },
    { id: 'earnings', label: 'Earnings', icon: FiDollarSign },
    { id: 'locations', label: 'Locations', icon: FiMapPin }
  ];

  return (
    <div className="improved-ranking">
      {/* Tabs */}
      <div className="ranking-tabs">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              className={`ranking-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent className="tab-icon" size={16} />
              <span className="tab-label">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="ranking-content"
        >
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="overview-content">
              <div className="stats-grid">
                <StatCard
                  label="Active Sensors"
                  value={processedData.stats.totalActive}
                  icon={FiBarChart2}
                  color="#00ff88"
                  lastUpdate={processedData.stats.lastUpdate}
                />
                <StatCard
                  label="Avg Quality"
                  value={`${processedData.stats.avgQuality > 0 ? processedData.stats.avgQuality.toFixed(2) : '0.00'}%`}
                  icon={FiTrendingUp}
                  color="#00d4ff"
                  lastUpdate={processedData.stats.lastUpdate}
                />
                <StatCard
                  label="Avg Temp"
                  value={`${processedData.stats.avgTemp > 0 ? processedData.stats.avgTemp.toFixed(2) : '0.00'}°C`}
                  icon={FiThermometer}
                  color="#ffeb3b"
                  lastUpdate={processedData.stats.lastUpdate}
                />
                <StatCard
                  label="Total Earnings"
                  value="0"
                  icon={FiDollarSign}
                  color="#9c27b0"
                  suffix="$STATS"
                  lastUpdate={processedData.stats.lastUpdate}
                />
              </div>

              {/* Quick Insights */}
              <div className="quick-insights">
                <h4>Quick Insights</h4>
                <div className="insight-list">
                  {processedData.alerts.length > 0 && (
                    <div className="insight-item alert">
                      <FiAlertTriangle className="insight-icon" size={16} />
                      <span>{processedData.alerts.length} sensors need attention</span>
                    </div>
                  )}
                  {processedData.stats.avgQuality > 70 && (
                    <div className="insight-item good">
                      <FiCheckCircle className="insight-icon" size={16} />
                      <span>Overall air quality is good</span>
                    </div>
                  )}
                  <div className="insight-item info">
                    <FiDollarSign className="insight-icon" size={16} />
                    <span>Token ainda não lançado</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quality Tab */}
          {activeTab === 'quality' && (
            <div className="quality-content">
              <div className="ranking-list-enhanced">
                {processedData.topPerformers.map((sensor, idx) => (
                  <QualityCard
                    key={sensor.id}
                    sensor={sensor}
                    rank={idx + 1}
                    activeLayer={activeLayer}
                    onClick={() => onSensorClick(sensor)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="alerts-content">
              {processedData.alerts.length === 0 ? (
                <div className="no-alerts">
                  <FiCheckCircle className="no-alerts-icon" size={32} />
                  <p>All sensors operating normally</p>
                </div>
              ) : (
                <div className="alert-list">
                  {processedData.alerts.map((sensor, idx) => (
                    <AlertCard
                      key={sensor.id}
                      sensor={sensor}
                      activeLayer={activeLayer}
                      onClick={() => onSensorClick(sensor)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Earnings Tab */}
          {activeTab === 'earnings' && (
            <div className="earnings-content">
              <div className="earnings-header">
                <h4>Top Earners</h4>
                <span className="total-distributed">
                  Total: 0 $STATS
                </span>
              </div>
              <div className="earnings-notice" style={{
                padding: '16px',
                margin: '16px 0',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                color: '#856404',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                <strong>Observação:</strong> O token ainda não foi lançado. Os valores de earnings serão atualizados após o lançamento.
              </div>
              <div className="earnings-list">
                {processedData.earningsLeaders.length === 0 ? (
                  <div style={{ 
                    padding: '32px', 
                    textAlign: 'center', 
                    color: '#999',
                    fontSize: '14px' 
                  }}>
                    Nenhum ganho registrado ainda
                  </div>
                ) : (
                  processedData.earningsLeaders.map((sensor, idx) => (
                    <EarningsCard
                      key={sensor.id}
                      sensor={sensor}
                      rank={idx + 1}
                      maxEarnings={1}
                      onClick={() => onSensorClick(sensor)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Locations Tab */}
          {activeTab === 'locations' && (
            <div className="locations-content">
              <div className="city-list">
                {Object.entries(processedData.cityDistribution)
                  .sort((a, b) => b[1].count - a[1].count)
                  .slice(0, 10)
                  .map(([city, data]) => (
                    <CityCard
                      key={city}
                      city={city}
                      data={data}
                      onClick={() => {
                        // Navegar para o primeiro sensor da cidade
                        if (data.sensors[0]) {
                          onSensorClick(data.sensors[0]);
                        }
                      }}
                    />
                  ))}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Componente StatCard
const StatCard = ({ label, value, icon: IconComponent, color, suffix, lastUpdate }) => (
  <motion.div 
    className="stat-card"
    whileHover={{ scale: 1.05 }}
    style={{ borderLeft: `3px solid ${color}` }}
  >
    <div className="stat-icon" style={{ color }}>
      <IconComponent size={20} />
    </div>
    <div className="stat-info">
      <span className="stat-label">{label}</span>
      <span className="stat-value">
        {value}
        {suffix && <span className="stat-suffix"> {suffix}</span>}
      </span>
      {lastUpdate && (
        <span className="stat-update" style={{
          fontSize: '10px',
          color: '#999',
          marginTop: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <FiClock size={10} />
          {formatLastUpdate(lastUpdate)}
        </span>
      )}
    </div>
  </motion.div>
);

// Componente QualityCard
const QualityCard = ({ sensor, rank, activeLayer, onClick }) => {
  // Para airQuality, converter AQI (0-300) para percentual de qualidade (0-100)
  // AQI menor = melhor qualidade = maior percentual
  let quality, qualityPercent;
  if (activeLayer === 'airQuality') {
    if (sensor.aqi != null) {
      // Converter AQI inversamente: AQI 0-50 (excelente) = 100%, AQI 300 (perigoso) = 0%
      quality = Math.max(0, 100 - (sensor.aqi / 3));
      qualityPercent = Math.max(0, Math.min(100, quality));
    } else {
      qualityPercent = 0;
    }
  } else {
    quality = sensor.quality;
    qualityPercent = Math.max(0, Math.min(100, quality || 0));
  }
  
  return (
    <motion.div
      className="quality-card"
      whileHover={{ x: 5 }}
      onClick={onClick}
    >
      <div className="quality-rank">
        <span className="rank-number">{rank}</span>
        {rank <= 3 && (
          <FiAward 
            className="trophy" 
            size={18}
            style={{ color: rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32' }}
          />
        )}
      </div>
      
      <div className="quality-info">
        <div className="quality-header">
          <span className="sensor-name">
            {sensor.business_name || sensor.location_name || sensor.city || 'Sensor'}
          </span>
          <span className="quality-value" style={{ 
            color: qualityPercent > 70 ? '#00ff88' : 
                   qualityPercent > 40 ? '#ffeb3b' : '#ff4444' 
          }}>
            {qualityPercent.toFixed(0)}%
          </span>
        </div>
        
        <div className="quality-bar">
          <motion.div 
            className="quality-fill"
            initial={{ width: 0 }}
            animate={{ width: `${qualityPercent}%` }}
            transition={{ duration: 0.5, delay: rank * 0.05 }}
            style={{
              background: `linear-gradient(90deg, 
                ${qualityPercent > 70 ? '#00ff88' : 
                  qualityPercent > 40 ? '#ffeb3b' : '#ff4444'} 0%, 
                ${qualityPercent > 70 ? '#00d4ff' : 
                  qualityPercent > 40 ? '#ff9800' : '#ff0000'} 100%)`
            }}
          />
        </div>
        
        <div className="quality-details">
          {activeLayer === 'airQuality' ? (
            <>
              <span className="detail-item">
                <FiWind size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                AQI: {sensor.aqi != null ? sensor.aqi.toFixed(0) : '--'}
              </span>
              <span className="detail-item">
                <FiThermometer size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                {sensor.externalTemperature != null ? sensor.externalTemperature.toFixed(2) : sensor.temperature != null ? sensor.temperature.toFixed(2) : '--'}°C
              </span>
              <span className="detail-item">
                <FiDroplet size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                PM2.5: {sensor.pm25 != null ? sensor.pm25.toFixed(2) : '--'} µg/m³
              </span>
            </>
          ) : (
            <>
              <span className="detail-item">
                <FiThermometer size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                {sensor.temperature != null ? sensor.temperature.toFixed(2) : '--'}°C
              </span>
              <span className="detail-item">
                <FiWind size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                {sensor.co2 != null ? sensor.co2.toFixed(2) : '--'} ppm
              </span>
              <span className="detail-item">
                <FiDroplet size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                {sensor.humidity != null ? sensor.humidity.toFixed(2) : '--'}%
              </span>
            </>
          )}
        </div>
        {sensor.lastUpdate && (
          <div className="quality-update-time" style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#999',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <FiClock size={12} />
            <span>Atualizado: {formatLastUpdate(sensor.lastUpdate)}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Componente AlertCard
const AlertCard = ({ sensor, activeLayer, onClick }) => {
  const getAlertReason = () => {
    const reasons = [];
    if (sensor.quality < 40) reasons.push('Low quality');
    if (sensor.co2 > 1000) reasons.push('High CO₂');
    if (sensor.temperature > 30) reasons.push('High temperature');
    if (sensor.temperature < 15) reasons.push('Low temperature');
    if (activeLayer === 'airQuality' && sensor.aqi > 100) reasons.push('Poor air quality');
    return reasons.join(', ');
  };
  
  return (
    <motion.div
      className="alert-card"
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
    >
      <div className="alert-indicator">
        <FiAlertTriangle size={20} />
      </div>
      <div className="alert-info">
        <span className="alert-location">
          {sensor.business_name || sensor.location_name || `Device ${sensor.device_id}`}
        </span>
        <span className="alert-reason">{getAlertReason()}</span>
        <div className="alert-metrics">
          {sensor.quality < 40 && (
            <span className="metric-badge bad">Quality: {sensor.quality != null ? sensor.quality.toFixed(2) : '--'}%</span>
          )}
          {sensor.co2 > 1000 && (
            <span className="metric-badge warning">CO₂: {sensor.co2 != null ? sensor.co2.toFixed(2) : '--'} ppm</span>
          )}
          {(sensor.temperature > 30 || sensor.temperature < 15) && (
            <span className="metric-badge warning">Temp: {sensor.temperature != null ? sensor.temperature.toFixed(2) : '--'}°C</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Componente EarningsCard
const EarningsCard = ({ sensor, rank, maxEarnings, onClick }) => {
  const earnings = 0; // Sempre 0 até o token ser lançado
  const percentage = 0;
  
  return (
    <motion.div
      className="earnings-card"
      whileHover={{ x: 5 }}
      onClick={onClick}
    >
      <div className="earnings-rank">#{rank}</div>
      <div className="earnings-info">
        <div className="earnings-header">
          <span className="earnings-name">
            {sensor.business_name || sensor.location_name || sensor.city || 'Sensor'}
          </span>
          <span className="earnings-amount">
            {earnings} $STATS
          </span>
        </div>
        <div className="earnings-bar">
          <motion.div
            className="earnings-fill"
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.5, delay: rank * 0.05 }}
          />
        </div>
        {sensor.wallet_address && (
          <span className="wallet-preview">
            {sensor.wallet_address.slice(0, 6)}...{sensor.wallet_address.slice(-4)}
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Componente CityCard
const CityCard = ({ city, data, onClick }) => (
  <motion.div
    className="city-card"
    whileHover={{ scale: 1.02 }}
    onClick={onClick}
  >
    <div className="city-header">
      <span className="city-name">{city}</span>
      <span className="city-count">{data.count} sensors</span>
    </div>
    <div className="city-stats">
      <div className="city-quality">
        <span className="quality-label">Avg Quality</span>
        <span className="quality-value" style={{
          color: data.avgQuality > 70 ? '#00ff88' : 
                 data.avgQuality > 40 ? '#ffeb3b' : '#ff4444'
        }}>
          {data.avgQuality.toFixed(2)}%
        </span>
      </div>
      <div className="city-bar">
        <div 
          className="city-bar-fill"
          style={{ 
            width: `${data.avgQuality}%`,
            background: data.avgQuality > 70 ? '#00ff88' : 
                       data.avgQuality > 40 ? '#ffeb3b' : '#ff4444'
          }}
        />
      </div>
    </div>
  </motion.div>
);

export default ImprovedRanking;
